import { Injectable, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { COLS, ROWS, idx, Frame, LedDesign, LivePayload } from '../models/led-board.model';
import { QueueEntry, NowShowing, DEFAULT_HOLD_MS } from '../models/led-queue.model';
import { SessionMeta, SessionPerson, DEFAULT_SESSION_MS } from '../models/led-session.model';

@Injectable({ providedIn: 'root' })
export class LedBoardService {
  private injector = inject(EnvironmentInjector);
  private inCtx<T>(fn: () => T): T { return runInInjectionContext(this.injector, fn); }

  constructor(private db: AngularFireDatabase) {}

  makeThumbnail(frame: Frame): string {
    if (typeof document === 'undefined') { return ''; }
    const canvas = document.createElement('canvas');
    canvas.width = COLS; canvas.height = ROWS;
    const ctx = canvas.getContext('2d');
    if (!ctx) { return ''; }
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) { ctx.fillStyle = frame[idx(x, y)]; ctx.fillRect(x, y, 1, 1); }
    }
    return canvas.toDataURL();
  }

  saveDesign(design: LedDesign) {
    return this.inCtx(() => this.db.list('/ledboard/designs').push(design));
  }

  listDesigns(): Observable<LedDesign[]> {
    return this.inCtx(() => this.db.list('/ledboard/designs').snapshotChanges().pipe(
      map(items => items.map(a => ({ key: a.payload.key, ...(a.payload.val() as any) }) as LedDesign))
    ));
  }

  deleteDesign(key: string) {
    return this.inCtx(() => this.db.list('/ledboard/designs/' + key).remove());
  }

  /** What the board is currently showing, as the Pi sees it. */
  watchLive(): Observable<LivePayload | null> {
    return this.inCtx(() => this.db.object<LivePayload>('/ledboard/live').valueChanges()) as Observable<LivePayload | null>;
  }

  sendToBoard(payload: LivePayload): Promise<void> {
    return this.inCtx(() => this.db.object('/ledboard/live').set(payload));
  }

  /**
   * Join the line. The board decides when this goes up, so the phone can be
   * put away the moment it's queued — that is the whole point of the queue.
   * Ordering follows the push key, which is already chronological.
   */
  async joinQueue(payload: LivePayload, meta: { name?: string; thumbnail?: string; holdMs?: number } = {}): Promise<string> {
    const entry: QueueEntry = {
      ...payload,
      holdMs: meta.holdMs ?? DEFAULT_HOLD_MS,
      submittedAt: Date.now(),
    };
    if (meta.name) { entry.name = meta.name; }
    if (meta.thumbnail) { entry.thumbnail = meta.thumbnail; }
    const ref = await this.inCtx(() => this.db.list('/ledboard/queue').push(entry));
    return ref.key as string;
  }

  /** Everyone waiting, oldest first. */
  watchQueue(): Observable<QueueEntry[]> {
    return this.inCtx(() => this.db.list('/ledboard/queue').snapshotChanges().pipe(
      map(items => items.map(a => ({ key: a.payload.key, ...(a.payload.val() as any) }) as QueueEntry))
    ));
  }

  /** What the board is showing and until when, as the Pi reports it. */
  watchNowShowing(): Observable<NowShowing | null> {
    return this.inCtx(() => this.db.object<NowShowing>('/ledboard/now').valueChanges()) as Observable<NowShowing | null>;
  }

  /** Give up a spot in the line. */
  leaveQueue(key: string): Promise<void> {
    return this.inCtx(() => this.db.object('/ledboard/queue/' + key).remove());
  }

  // ---- live sessions -------------------------------------------------------
  // A session is a turn in the same line, so it can't barge in front of the
  // designs already waiting. When its turn comes the board opens a shared
  // canvas at /ledboard/session and everyone paints into it at once.

  /** Queue a live session of the given length. */
  joinQueueAsSession(durationMs = DEFAULT_SESSION_MS, name?: string): Promise<string> {
    const entry: any = {
      kind: 'session',
      mode: 'session',
      durationMs,
      holdMs: durationMs,
      brightness: 15,
      updatedAt: Date.now(),
      submittedAt: Date.now(),
    };
    if (name) { entry.name = name; }
    return this.inCtx(() => this.db.list('/ledboard/queue').push(entry)).then(ref => ref.key as string);
  }

  /** The running session, or null when nobody has the board. */
  watchSession(): Observable<SessionMeta | null> {
    return this.inCtx(() => this.db.object<SessionMeta>('/ledboard/session/meta').valueChanges()) as Observable<SessionMeta | null>;
  }

  /** Every painted pixel in the shared canvas, keyed by index. */
  watchSessionPixels(): Observable<Record<string, string>> {
    return this.inCtx(() => this.db.object<Record<string, string>>('/ledboard/session/pixels').valueChanges()).pipe(
      map(v => v || {})
    );
  }

  /**
   * Paint into the shared canvas. Only changed pixels are sent, so two people
   * drawing at once merge instead of overwriting each other.
   */
  paintSession(changes: Record<string, string>): Promise<void> {
    if (!Object.keys(changes).length) { return Promise.resolve(); }
    return this.inCtx(() => this.db.object('/ledboard/session/pixels').update(changes));
  }

  /** Who else is drawing, and where they are working. */
  watchSessionPeople(): Observable<Record<string, SessionPerson>> {
    return this.inCtx(() => this.db.object<Record<string, SessionPerson>>('/ledboard/session/people').valueChanges()).pipe(
      map(v => v || {})
    );
  }

  /**
   * Announce this browser as one of the people drawing, and have the server
   * drop it when the tab closes — otherwise a crowd of ghosts builds up.
   */
  async enterSession(clientId: string, color: string): Promise<void> {
    const ref = this.inCtx(() => this.db.object('/ledboard/session/people/' + clientId));
    await ref.set({ color, at: Date.now() } as SessionPerson);
    try { await ref.query.ref.onDisconnect().remove(); } catch { /* presence is best-effort */ }
  }

  /** Move this person's cursor, so the others can see where they are painting. */
  markSessionCell(clientId: string, cell: number): Promise<void> {
    return this.inCtx(() => this.db.object('/ledboard/session/people/' + clientId).update({ cell, at: Date.now() }));
  }

  leaveSession(clientId: string): Promise<void> {
    return this.inCtx(() => this.db.object('/ledboard/session/people/' + clientId).remove());
  }
}
